import { ApiEnvelope, JiraIssue } from '../../domain/types';
import type { JiraConnectionAck } from '@zunelo/shared';
import { IJiraClient, HttpClientOptions } from './types';
import { createHttpTransport, type HttpTransport } from './httpTransport';
import { parseEnvelope } from './parseEnvelope';

export class HttpJiraClient implements IJiraClient {
  private readonly transport: HttpTransport;

  constructor(options: HttpClientOptions | string = {}) {
    this.transport = createHttpTransport(options);
  }

  private async request<T>(path: string, options?: RequestInit): Promise<ApiEnvelope<T>> {
    const res = await this.transport.fetch(path, options);
    return parseEnvelope<T>(res, undefined, 'JIRA_HTTP_ERROR');
  }

  async getIssues(): Promise<ApiEnvelope<JiraIssue[]>> {
    return this.request<JiraIssue[]>('/api/jira/issues');
  }

  async getIssue(key: string): Promise<ApiEnvelope<JiraIssue | null>> {
    return this.request<JiraIssue | null>(`/api/jira/issues/${encodeURIComponent(key)}`);
  }

  async updateWorkflowState(key: string, newState: string): Promise<ApiEnvelope<JiraIssue>> {
    return this.request<JiraIssue>(`/api/jira/issues/${encodeURIComponent(key)}/transition`, {
      method: 'POST',
      body: JSON.stringify({ newState }),
    });
  }

  async toggleCriterion(key: string, criterionIndex: number): Promise<ApiEnvelope<JiraIssue>> {
    return this.request<JiraIssue>(
      `/api/jira/issues/${encodeURIComponent(key)}/criteria/${criterionIndex}/toggle`,
      {
        method: 'POST',
      },
    );
  }

  async addAcceptanceCriterion(key: string, text: string): Promise<ApiEnvelope<JiraIssue>> {
    return this.request<JiraIssue>(`/api/jira/issues/${encodeURIComponent(key)}/criteria`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  async updateAcceptanceCriterion(
    key: string,
    criterionIndex: number,
    text: string,
  ): Promise<ApiEnvelope<JiraIssue>> {
    return this.request<JiraIssue>(
      `/api/jira/issues/${encodeURIComponent(key)}/criteria/${criterionIndex}`,
      {
        method: 'PUT',
        body: JSON.stringify({ text }),
      },
    );
  }

  async deleteAcceptanceCriterion(
    key: string,
    criterionIndex: number,
  ): Promise<ApiEnvelope<JiraIssue>> {
    return this.request<JiraIssue>(
      `/api/jira/issues/${encodeURIComponent(key)}/criteria/${criterionIndex}`,
      {
        method: 'DELETE',
      },
    );
  }

  async cycleSubtask(key: string, subtaskIndex: number): Promise<ApiEnvelope<JiraIssue>> {
    return this.request<JiraIssue>(
      `/api/jira/issues/${encodeURIComponent(key)}/subtasks/${subtaskIndex}/cycle`,
      {
        method: 'POST',
      },
    );
  }

  async getConnection(): Promise<ApiEnvelope<JiraConnectionAck>> {
    return this.request<JiraConnectionAck>('/api/jira/connection');
  }

  async testConnection(): Promise<ApiEnvelope<JiraConnectionAck>> {
    return this.request<JiraConnectionAck>('/api/jira/connection/test', { method: 'POST' });
  }

  async generateAiSummary(key: string): Promise<ApiEnvelope<JiraIssue>> {
    return this.request<JiraIssue>(`/api/jira/issues/${encodeURIComponent(key)}/summary`, {
      method: 'POST',
    });
  }

  async addComment(
    key: string,
    markdown: string,
    idempotencyKey?: string,
  ): Promise<ApiEnvelope<unknown>> {
    return this.request<unknown>(`/api/jira/issues/${encodeURIComponent(key)}/comments`, {
      method: 'POST',
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
      body: JSON.stringify({ markdown }),
    });
  }

  async generateCommentDraft(
    key: string,
    hint?: string,
  ): Promise<ApiEnvelope<{ draft: string; provider?: string; model?: string }>> {
    return this.request<{ draft: string; provider?: string; model?: string }>(
      `/api/jira/issues/${encodeURIComponent(key)}/comments/draft`,
      {
        method: 'POST',
        body: JSON.stringify({ hint }),
      },
    );
  }
}
